import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  InputBase,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import SearchIcon from '@mui/icons-material/Search';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';

import { SecondBrainChat } from '../types';
import { ChatDayBucket, formatRelativeTime, getChatDayBucket } from '../utils/relativeTime';
import { smallTagChipSx } from './tagChipStyles';

const TOP_TAG_FILTERS_LIMIT = 5;
const DAY_BUCKET_ORDER: ChatDayBucket[] = ['Today', 'Yesterday', 'Earlier'];

interface HistoryPaletteProps {
  open: boolean;
  chats: SecondBrainChat[];
  activeChatId: string | null;
  isLoading: boolean;
  onClose: () => void;
  onSelectChat: (chatId: string) => void;
  onDeleteChat: (chatId: string, event: React.MouseEvent) => void;
  onNewChat: () => void;
}

interface PaletteChatItem {
  chat: SecondBrainChat;
  citedMatch?: string;
}

const getChatTitle = (chat: SecondBrainChat) =>
  chat.question ?? chat.messages.find((message) => message.role === 'user')?.content ?? 'Untitled chat';

const getChatMeta = (chat: SecondBrainChat) => {
  const noteCount = chat.contextNotes?.length ?? 0;
  const relative = formatRelativeTime(chat.createdAt);
  const parts = [] as string[];
  if (noteCount > 0) {
    parts.push(`${noteCount} note${noteCount === 1 ? '' : 's'}`);
  }
  if (relative) {
    parts.push(relative);
  }
  return parts.join(' · ');
};

export const HistoryPalette = ({
  open,
  chats,
  activeChatId,
  isLoading,
  onClose,
  onSelectChat,
  onDeleteChat,
  onNewChat,
}: HistoryPaletteProps) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [query, setQuery] = useState('');
  const [tagFilters, setTagFilters] = useState<string[]>([]);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const normalizedQuery = query.trim().toLowerCase();

  const topTags = useMemo(() => {
    const counts = new Map<string, number>();
    chats.forEach((chat) => chat.tags.forEach((tag) => counts.set(tag, (counts.get(tag) ?? 0) + 1)));
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, TOP_TAG_FILTERS_LIMIT)
      .map(([tag]) => tag);
  }, [chats]);

  const filteredItems = useMemo(() => {
    return chats
      .filter((chat) => (tagFilters.length === 0 ? true : chat.tags.some((tag) => tagFilters.includes(tag))))
      .map((chat): PaletteChatItem | null => {
        if (!normalizedQuery) {
          return { chat };
        }
        if (getChatTitle(chat).toLowerCase().includes(normalizedQuery)) {
          return { chat };
        }
        const citedNote = chat.contextNotes?.find((note) => (note.title ?? '').toLowerCase().includes(normalizedQuery));
        return citedNote ? { chat, citedMatch: citedNote.title ?? undefined } : null;
      })
      .filter((item): item is PaletteChatItem => Boolean(item));
  }, [chats, normalizedQuery, tagFilters]);

  const groups = useMemo(() => {
    const byBucket = new Map<ChatDayBucket, PaletteChatItem[]>();
    filteredItems.forEach((item) => {
      const bucket = getChatDayBucket(item.chat.createdAt);
      byBucket.set(bucket, [...(byBucket.get(bucket) ?? []), item]);
    });
    return DAY_BUCKET_ORDER.map((label) => ({ label, items: byBucket.get(label) ?? [] })).filter(
      (group) => group.items.length > 0,
    );
  }, [filteredItems]);

  const flatChatItems = useMemo(() => groups.flatMap((group) => group.items), [groups]);
  const totalSelectable = flatChatItems.length + 1; // +1 for "Start a new chat"

  useEffect(() => {
    setHighlightIndex(0);
  }, [normalizedQuery, tagFilters, open]);

  // Clear search state on close no matter how it closed (esc, backdrop, ⌘K toggle)
  useEffect(() => {
    if (!open) {
      setQuery('');
      setTagFilters([]);
    }
  }, [open]);

  // Keep the highlight in range when the list shrinks (e.g. after a delete)
  useEffect(() => {
    setHighlightIndex((index) => Math.min(index, flatChatItems.length));
  }, [flatChatItems.length]);

  useEffect(() => {
    const highlighted = listRef.current?.querySelector<HTMLElement>(`[data-palette-index="${highlightIndex}"]`);
    highlighted?.scrollIntoView({ block: 'nearest' });
  }, [highlightIndex]);

  const handleToggleTagFilter = (tag: string) => {
    setTagFilters((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightIndex((index) => Math.min(index + 1, totalSelectable - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (highlightIndex === 0) {
        onNewChat();
        return;
      }
      const item = flatChatItems[highlightIndex - 1];
      if (item) {
        onSelectChat(item.chat.id);
      }
    }
  };

  let flatIndexCursor = 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth={false}
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start', pt: fullScreen ? 0 : '104px' } }}
      PaperProps={{
        onKeyDown: handleKeyDown,
        sx: {
          width: 660,
          maxWidth: '100%',
          maxHeight: fullScreen ? '100%' : 600,
          m: fullScreen ? 0 : 2,
          borderRadius: fullScreen ? 0 : '16px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={1.5}
        sx={{ p: '14px 18px', borderBottom: '1px solid', borderColor: 'neutral.otherBorder', flex: 'none' }}
      >
        <SearchIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
        <InputBase
          autoFocus
          fullWidth
          placeholder="Search chats and the notes they cited…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          sx={{ fontSize: 15 }}
        />
        <Box
          component="span"
          sx={{
            display: { xs: 'none', sm: 'inline-block' },
            fontFamily: 'ui-monospace, Menlo, monospace',
            fontSize: 11,
            fontWeight: 500,
            color: 'grey.500',
            border: '1px solid',
            borderColor: 'neutral.otherBorder',
            borderRadius: '4px',
            px: 0.75,
            py: 0.5,
            lineHeight: 1,
          }}
        >
          esc
        </Box>
      </Stack>

      {topTags.length > 0 && (
        <Stack
          direction="row"
          alignItems="center"
          gap={0.75}
          flexWrap="wrap"
          sx={{
            px: '18px',
            py: '10px',
            borderBottom: '1px solid',
            borderColor: 'neutral.otherBorder',
            bgcolor: 'grey.50',
            flex: 'none',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Filter
          </Typography>
          {topTags.map((tag) => {
            const isActive = tagFilters.includes(tag);
            return (
              <Chip
                key={tag}
                label={tag}
                size="small"
                onClick={() => handleToggleTagFilter(tag)}
                sx={{
                  height: 24,
                  fontSize: 12,
                  borderRadius: '4px',
                  bgcolor: isActive ? 'primary.main' : 'grey.100',
                  color: isActive ? 'common.white' : 'text.primary',
                  '&:hover': { bgcolor: isActive ? 'primary.dark' : 'grey.200' },
                }}
              />
            );
          })}
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {filteredItems.length} chat{filteredItems.length === 1 ? '' : 's'}
          </Typography>
        </Stack>
      )}

      <Box ref={listRef} sx={{ overflow: 'auto', p: '8px 8px 12px', flex: 1 }}>
        <ButtonBase
          data-palette-index={0}
          onClick={onNewChat}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 1.5,
            width: '100%',
            p: '10px 12px',
            borderRadius: '8px',
            bgcolor: highlightIndex === 0 ? 'rgba(43, 159, 255, 0.2)' : 'rgba(43, 159, 255, 0.12)',
            color: 'primary.main',
            textAlign: 'left',
          }}
        >
          <AddIcon sx={{ fontSize: 20 }} />
          <Typography sx={{ flex: 1, fontWeight: 500, fontSize: 14 }}>Start a new chat</Typography>
        </ButtonBase>

        {isLoading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
            <CircularProgress size={24} />
          </Stack>
        ) : flatChatItems.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 3 }}>
            {chats.length === 0
              ? 'No chats yet. Ask your first question to get started.'
              : 'No chats match your search.'}
          </Typography>
        ) : (
          groups.map((group) => (
            <Fragment key={group.label}>
              <Box sx={{ p: '14px 12px 6px' }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: 11,
                    lineHeight: '16px',
                    letterSpacing: '0.6px',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                  }}
                >
                  {group.label}
                </Typography>
              </Box>
              {group.items.map((item) => {
                flatIndexCursor += 1;
                const paletteIndex = flatIndexCursor;
                const isHighlighted = highlightIndex === paletteIndex;
                const isActive = item.chat.id === activeChatId;
                return (
                  <Box
                    key={item.chat.id}
                    data-palette-index={paletteIndex}
                    onClick={() => onSelectChat(item.chat.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      width: '100%',
                      p: '9px 12px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      bgcolor: isHighlighted ? 'grey.100' : 'transparent',
                      '&:hover': { bgcolor: 'grey.100' },
                      '&:hover .palette-delete': { opacity: 1 },
                    }}
                  >
                    <ForumOutlinedIcon
                      sx={{ fontSize: 18, color: isActive ? 'primary.main' : 'text.secondary', mt: 0.25 }}
                    />
                    <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.25}>
                      <Typography variant="body2" fontWeight={500} noWrap>
                        {getChatTitle(item.chat)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {getChatMeta(item.chat)}
                      </Typography>
                      {item.citedMatch && (
                        <Stack direction="row" alignItems="center" gap={0.75} sx={{ mt: 0.25, minWidth: 0 }}>
                          <SubdirectoryArrowRightIcon sx={{ fontSize: 14, color: 'grey.500', flex: 'none' }} />
                          <Typography variant="caption" color="text.secondary" noWrap>
                            cited {item.citedMatch}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                    {item.chat.tags.slice(0, 1).map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        sx={{ ...smallTagChipSx(0), mt: 0.25, display: { xs: 'none', sm: 'inline-flex' } }}
                      />
                    ))}
                    <Tooltip title="Delete chat">
                      <IconButton
                        className="palette-delete"
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          onDeleteChat(item.chat.id, event);
                        }}
                        sx={{ opacity: { xs: 1, sm: 0 }, transition: 'opacity 150ms ease', mt: -0.25 }}
                        aria-label="Delete chat"
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                );
              })}
            </Fragment>
          ))
        )}
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        gap={2}
        sx={{
          px: '18px',
          py: '10px',
          borderTop: '1px solid',
          borderColor: 'neutral.otherBorder',
          bgcolor: 'grey.50',
          flex: 'none',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          ↑↓ navigate
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ↵ open
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
          Searches titles and cited notes
        </Typography>
      </Stack>
    </Dialog>
  );
};

export default HistoryPalette;
